import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2, Sparkles, Home, Package, Gem, Award, Crown,
  ArrowRight, RefreshCw, Gift, X, CalendarDays, Repeat,
  UserCheck, Clock3, ThumbsUp, CalendarClock, ShieldCheck, XCircle,
} from "lucide-react";
import {
  CORE_PLANS, EXTRA_PLANS, formatInr, formatVisits, formatServicesIncluded,
} from "./membershipPlansData";
import { getServiceImage, DEFAULT_HERO_IMAGE } from "../../config/serviceImages";

const fallbackImage = DEFAULT_HERO_IMAGE;

const PLAN_ICONS = { Package, Home, Gem, Award, Crown };

const TRUST_ITEMS = [
  { icon: UserCheck,     title: "Trained Professionals", text: "Background verified experts" },
  { icon: Clock3,        title: "On-time Service",        text: "Punctual & reliable" },
  { icon: ThumbsUp,      title: "100% Satisfaction",      text: "Quality service, every time" },
  { icon: CalendarClock, title: "Easy Rescheduling",      text: "Flexible & convenient" },
  { icon: ShieldCheck,   title: "Secure Payments",        text: "Safe & trusted" },
];

// ── Plan card — theme "purple" (Recurring) or "gold" (Package) drives the
// highlighted-card accent so each section reads as visually distinct. ──────
const PlanCard = ({ plan, theme, onDetails }) => {
  const Icon = PLAN_ICONS[plan.icon] || Package;
  const accent = theme === "gold" ? T.gold : T.purple;

  return (
    <div style={{ ...S.card, ...(plan.highlight ? { ...S.cardPopular, borderColor: accent.border, boxShadow: `0 16px 34px ${accent.shadow}` } : {}) }}>
      {plan.highlight && (
        <div style={{ ...S.popularRibbon, background: accent.solid, boxShadow: `0 6px 16px ${accent.shadow}` }}>
          <Sparkles size={12} /> Most Popular
        </div>
      )}

      <div style={S.planHeadRow}>
        <div style={{ ...S.planIconWrap, ...(plan.highlight ? { background: accent.solid } : {}) }}>
          <Icon size={20} color={plan.highlight ? "#fff" : accent.solid} />
        </div>
        <div>
          <h3 style={S.planName}>{plan.name}</h3>
          <p style={S.planTagline}>{plan.tagline}</p>
        </div>
      </div>

      <div style={S.planPriceRow}>
        <span style={S.planPrice}>{formatInr(plan.price)}</span>
        <span style={S.planPeriod}>{plan.period}</span>
      </div>

      <div style={S.planDivider} />

      <ul style={S.featureList}>
        {plan.features.map((f) => (
          <li key={f} style={S.featureItem}>
            <CheckCircle2 size={15} color={accent.solid} />
            <span style={S.featureText}>{f}</span>
          </li>
        ))}
      </ul>

      <button
        type="button"
        style={{ ...S.detailsBtn, color: accent.solid, border: `1px solid ${accent.solid}` }}
        onClick={() => onDetails(plan)}
      >
        Details
      </button>

      <Link
        to={`/subscription-plans/checkout/${plan.id}`}
        style={{
          ...S.planCta,
          ...(plan.highlight
            ? { background: accent.solid, color: "#fff", border: `1px solid ${accent.solid}`, boxShadow: `0 10px 22px ${accent.shadow}` }
            : { color: accent.solid, border: `1px solid ${accent.solid}` }),
          textAlign: "center", textDecoration: "none", display: "block", boxSizing: "border-box",
        }}
      >
        {plan.cta}
      </Link>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PLAN DETAILS MODAL — image slider, full price/visit/duration breakdown,
// and the complete list of included services.
// ─────────────────────────────────────────────────────────────────────────────
const PlanDetailsModal = ({ plan, theme, onClose }) => {
  const [slide, setSlide] = useState(0);
  if (!plan) return null;

  const accent = theme === "gold" ? T.gold : T.purple;
  const images = Array.isArray(plan.images) && plan.images.length ? plan.images : [fallbackImage];
  const Icon = PLAN_ICONS[plan.icon] || Package;

  // weekly-care's includedServices is now its real, complete per-visit
  // scope (11 specific tasks) rather than a rotating task-type pool, so the
  // real list length is the accurate "Services Total" — not the generic
  // visitsCount*SERVICES_PER_VISIT estimate other plans still rely on
  // (monthly-care/premium-family keep using that estimate, unaffected).
  const totalServicesLabel = plan.servicesAreDefinitive && plan.includedServices?.length
    ? `${plan.includedServices.length} Services`
    : formatServicesIncluded(plan);

  const prev = () => setSlide((s) => (s - 1 + images.length) % images.length);
  const next = () => setSlide((s) => (s + 1) % images.length);

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
        <button type="button" style={S.modalCloseBtn} onClick={onClose}><X size={16} /></button>

        {/* Image slider */}
        <div style={S.modalSliderWrap}>
          <img
            src={images[slide]}
            alt={`${plan.name} ${slide + 1}`}
            style={S.modalSliderImg}
            onError={(e) => { e.currentTarget.src = fallbackImage; }}
          />
          {images.length > 1 && (
            <>
              <button type="button" style={{ ...S.modalArrow, left: 10 }} onClick={prev}>‹</button>
              <button type="button" style={{ ...S.modalArrow, right: 10 }} onClick={next}>›</button>
              <div style={S.modalDots}>
                {images.map((_, i) => (
                  <button
                    key={i} type="button"
                    style={{ ...S.modalDot, ...(i === slide ? { background: "#fff" } : {}) }}
                    onClick={() => setSlide(i)}
                  />
                ))}
              </div>
            </>
          )}
          {plan.highlight && (
            <div style={{ ...S.modalPopularTag, background: accent.solid }}><Sparkles size={12} /> Most Popular</div>
          )}
        </div>

        <div style={S.modalBody}>
          <div style={S.modalHeadRow}>
            <div style={{ ...S.planIconWrap, background: accent.tint }}>
              <Icon size={22} color={accent.solid} />
            </div>
            <div>
              <h2 style={S.modalTitle}>{plan.name}</h2>
              <p style={S.planTagline}>{plan.tagline}</p>
            </div>
          </div>

          {/* Price + visit/duration stat row — some plans (e.g. Bronze)
              skip both entirely and go straight to Services Included. */}
          {!plan.hideDetailStats && (
            <div style={S.modalPriceRow}>
              <span style={S.modalPrice}>{formatInr(plan.price)}</span>
              <span style={S.planPeriod}>{plan.period}</span>
            </div>
          )}

          {!plan.hideDetailStats && (
            <div style={S.modalStatsRow}>
              <div style={S.modalStat}>
                <CalendarDays size={16} color={accent.solid} />
                <div>
                  <div style={S.modalStatVal}>{plan.durationDays} Days</div>
                  <div style={S.modalStatLabel}>Plan Duration</div>
                </div>
              </div>
              <div style={S.modalStat}>
                <Repeat size={16} color={accent.solid} />
                <div>
                  <div style={S.modalStatVal}>{formatVisits(plan.visitsCount)}</div>
                  <div style={S.modalStatLabel}>Included</div>
                </div>
              </div>
              <div style={S.modalStat}>
                <CheckCircle2 size={16} color={accent.solid} />
                <div>
                  <div style={S.modalStatVal}>{totalServicesLabel}</div>
                  <div style={S.modalStatLabel}>Services Total</div>
                </div>
              </div>
            </div>
          )}

          {/* Services included — one photo card per task, same registered
              image reused everywhere that task appears across the app. */}
          {Array.isArray(plan.includedServices) && plan.includedServices.length > 0 && (
            <div style={S.modalSection}>
              <div style={S.modalSectionTitle}>Services Included</div>
              <div style={S.serviceImageGrid}>
                {plan.includedServices.map((s) => (
                  <div key={s} style={S.serviceImageCard}>
                    <img
                      src={getServiceImage(s)}
                      alt={s}
                      style={S.serviceImageThumb}
                      onError={(e) => { e.currentTarget.src = fallbackImage; }}
                    />
                    <div style={{ ...S.serviceImageLabel, color: accent.solid, background: accent.tint }}>
                      <CheckCircle2 size={11} /> {s}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Services excluded — same photo-card treatment as Included,
              muted/red so it reads as "not covered" at a glance. */}
          {Array.isArray(plan.excludedServices) && plan.excludedServices.length > 0 && (
            <div style={S.modalSection}>
              <div style={S.modalSectionTitle}>Services Excluded</div>
              <div style={S.serviceImageGrid}>
                {plan.excludedServices.map((s) => (
                  <div key={s} style={{ ...S.serviceImageCard, opacity: 0.85 }}>
                    <img
                      src={getServiceImage(s)}
                      alt={s}
                      style={{ ...S.serviceImageThumb, filter: "grayscale(55%)" }}
                      onError={(e) => { e.currentTarget.src = fallbackImage; }}
                    />
                    <div style={{ ...S.serviceImageLabel, color: "#991b1b", background: "#fef2f2" }}>
                      <XCircle size={11} /> {s}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Full plan features */}
          <div style={S.modalSection}>
            <div style={S.modalSectionTitle}>Plan Benefits</div>
            <ul style={S.featureList}>
              {plan.features.map((f) => (
                <li key={f} style={S.featureItem}>
                  <CheckCircle2 size={15} color={accent.solid} />
                  <span style={S.featureText}>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          <Link
            to={`/subscription-plans/checkout/${plan.id}`}
            style={{ ...S.planCta, background: accent.solid, color: "#fff", border: `1px solid ${accent.solid}`, textAlign: "center", textDecoration: "none", display: "block", boxSizing: "border-box" }}
            onClick={onClose}
          >
            {plan.cta}
          </Link>
        </div>
      </div>
    </div>
  );
};

const SubscriptionPlans = () => {
  const [activeSection, setActiveSection] = useState("recurring");
  const [detailsPlan, setDetailsPlan]   = useState(null);
  const [detailsTheme, setDetailsTheme] = useState("purple");
  const recurringRef = useRef(null);
  const packageRef   = useRef(null);

  const goTo = (section) => {
    setActiveSection(section);
    const ref = section === "recurring" ? recurringRef : packageRef;
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openDetails = (plan, theme) => { setDetailsPlan(plan); setDetailsTheme(theme); };

  return (
    <div style={S.page}>
      <div style={S.container}>
        <div style={S.breadcrumb}>
          <Link to="/" style={S.breadcrumbLink}>Home</Link> <span>&gt;</span> <span>Subscription Plans</span>
        </div>

        {/* Hero */}
        <div style={S.hero}>
          <h1 style={S.heroTitle}>
            Choose Your <span style={S.heroTitleAccent}>Perfect Plan</span>
          </h1>
          <p style={S.heroText}>
            Pick the plan that fits your needs and enjoy priority service,
            exclusive discounts, and hassle-free home care.
          </p>
        </div>

        {/* Section nav — both sections are always shown below; these just
            highlight + scroll to whichever the user is focused on. */}
        <div style={S.navRow}>
          <button
            type="button"
            style={{ ...S.navCard, ...(activeSection === "recurring" ? S.navCardActive : {}) }}
            onClick={() => goTo("recurring")}
          >
            <div style={{ ...S.navIconWrap, ...(activeSection === "recurring" ? { background: T.purple.solid } : {}) }}>
              <RefreshCw size={18} color={activeSection === "recurring" ? "#fff" : T.purple.solid} />
            </div>
            <div>
              <div style={{ ...S.navTitle, ...(activeSection === "recurring" ? { color: T.purple.solid } : {}) }}>Recurring Plans</div>
              <div style={S.navSub}>Pay less, stay covered</div>
            </div>
          </button>

          <button
            type="button"
            style={{ ...S.navCard, ...(activeSection === "package" ? S.navCardActive : {}) }}
            onClick={() => goTo("package")}
          >
            <div style={{ ...S.navIconWrap, ...(activeSection === "package" ? { background: T.gold.solid } : {}) }}>
              <Gift size={18} color={activeSection === "package" ? "#fff" : T.gold.solid} />
            </div>
            <div>
              <div style={{ ...S.navTitle, ...(activeSection === "package" ? { color: T.gold.solid } : {}) }}>Package Plans</div>
              <div style={S.navSub}>Bigger value, more benefits</div>
            </div>
          </button>
        </div>

        {/* ── Recurring Plans ─────────────────────────────────────────────── */}
        <section ref={recurringRef} style={S.sectionBlock}>
          <div style={S.sectionHead}>
            <div style={S.sectionHeadLeft}>
              <div style={{ ...S.sectionIconWrap, background: T.purple.tint }}>
                <RefreshCw size={18} color={T.purple.solid} />
              </div>
              <div>
                <h2 style={S.sectionTitle}>Recurring Plans</h2>
                <p style={S.sectionDesc}>Choose a plan that renews automatically and keeps your home worry-free.</p>
              </div>
            </div>
            <span style={{ ...S.sectionBadge, color: T.purple.solid, background: T.purple.tint }}>Best for regular maintenance</span>
          </div>

          <div style={S.grid}>
            {CORE_PLANS.map((plan) => (
              <PlanCard key={plan.id} plan={plan} theme="purple" onDetails={(p) => openDetails(p, "purple")} />
            ))}
          </div>
        </section>

        {/* ── Package Plans ───────────────────────────────────────────────── */}
        <section ref={packageRef} style={S.sectionBlock}>
          <div style={S.sectionHead}>
            <div style={S.sectionHeadLeft}>
              <div style={{ ...S.sectionIconWrap, background: "#f3f4f6" }}>
                <Gift size={18} color="#6b7280" />
              </div>
              <div>
                <h2 style={S.sectionTitle}>Package Plans</h2>
                <p style={S.sectionDesc}>One-time subscription packs with maximum value and premium benefits.</p>
              </div>
            </div>
            <span style={{ ...S.sectionBadge, color: T.gold.solid, background: T.gold.tint }}>Best value · More savings</span>
          </div>

          <div style={S.grid}>
            {EXTRA_PLANS.map((plan) => (
              <PlanCard key={plan.id} plan={plan} theme="gold" onDetails={(p) => openDetails(p, "gold")} />
            ))}
          </div>
        </section>

        {/* Trust strip */}
        <div style={S.trustStrip}>
          {TRUST_ITEMS.map((t) => {
            const Icon = t.icon;
            return (
              <div key={t.title} style={S.trustItem}>
                <div style={S.trustIconWrap}><Icon size={18} color={T.purple.solid} /></div>
                <div>
                  <div style={S.trustTitle}>{t.title}</div>
                  <div style={S.trustText}>{t.text}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Support strip */}
        <div style={S.supportBox}>
          <div>
            <h3 style={S.supportTitle}>Not sure which plan fits your home?</h3>
            <p style={S.supportText}>Our team can help you pick the right membership in under 2 minutes.</p>
          </div>
          <Link to="/contact-us" style={S.supportBtn}>
            Talk to Us <ArrowRight size={16} />
          </Link>
        </div>
      </div>

      <PlanDetailsModal
        plan={detailsPlan}
        theme={detailsTheme}
        onClose={() => setDetailsPlan(null)}
      />
    </div>
  );
};

// ── Section accent themes ───────────────────────────────────────────────────
const T = {
  purple: { solid: "#4f46e5", tint: "#eef2ff", border: "#4f46e5", shadow: "rgba(79,70,229,0.28)" },
  gold:   { solid: "#d97706", tint: "#fef3c7", border: "#d97706", shadow: "rgba(217,119,6,0.28)" },
};

const S = {
  page:      { background: "#fafafa", minHeight: "100vh", padding: "28px 20px 60px", fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif", color: "#111827" },
  container: { maxWidth: "1300px", margin: "0 auto" },
  breadcrumb:     { fontSize: "13px", color: "#9ca3af", marginBottom: "22px", display: "flex", gap: "6px" },
  breadcrumbLink: { color: "#4f46e5", fontWeight: 600, textDecoration: "none" },

  hero:            { textAlign: "center", maxWidth: "680px", margin: "0 auto 30px" },
  heroTitle:       { margin: 0, fontSize: "38px", fontWeight: 800, letterSpacing: "-0.6px", color: "#111827" },
  heroTitleAccent: { color: "#4f46e5" },
  heroText:        { margin: "14px 0 0", color: "#6b7280", fontSize: "15px", lineHeight: 1.7 },

  navRow:  { display: "flex", justifyContent: "center", gap: "16px", flexWrap: "wrap", margin: "0 0 36px" },
  navCard: { display: "flex", alignItems: "center", gap: "12px", background: "#fff", border: "1.5px solid #e5e7eb", borderRadius: "16px", padding: "14px 22px", cursor: "pointer", minWidth: "280px", textAlign: "left" },
  navCardActive: { border: "1.5px solid #4f46e5", boxShadow: "0 8px 20px rgba(79,70,229,0.12)" },
  navIconWrap: { width: "38px", height: "38px", borderRadius: "10px", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  navTitle: { fontSize: "15px", fontWeight: 800, color: "#111827" },
  navSub:   { fontSize: "12px", color: "#9ca3af", marginTop: "2px" },

  sectionBlock: { marginBottom: "40px" },
  sectionHead:  { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "14px", marginBottom: "20px", flexWrap: "wrap" },
  sectionHeadLeft: { display: "flex", alignItems: "flex-start", gap: "12px" },
  sectionIconWrap: { width: "40px", height: "40px", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  sectionTitle: { margin: 0, fontSize: "20px", fontWeight: 800, color: "#111827" },
  sectionDesc:  { margin: "4px 0 0", fontSize: "13px", color: "#6b7280" },
  sectionBadge: { fontSize: "12px", fontWeight: 700, padding: "7px 14px", borderRadius: "999px", whiteSpace: "nowrap" },

  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(270px, 1fr))", gap: "20px", alignItems: "stretch" },

  card:        { position: "relative", background: "#fff", border: "1px solid #ececec", borderRadius: "20px", padding: "26px 22px", display: "flex", flexDirection: "column", boxShadow: "0 6px 20px rgba(15,23,42,0.05)" },
  cardPopular: { borderWidth: "2px", borderStyle: "solid" },
  popularRibbon: { position: "absolute", top: "-13px", left: "50%", transform: "translateX(-50%)", display: "inline-flex", alignItems: "center", gap: "5px", color: "#fff", fontSize: "11px", fontWeight: 800, padding: "6px 14px", borderRadius: "999px", whiteSpace: "nowrap" },

  planHeadRow: { display: "flex", alignItems: "flex-start", gap: "12px", marginBottom: "18px" },
  planIconWrap: { width: "42px", height: "42px", borderRadius: "12px", background: "#eef2ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  planName:    { margin: 0, fontSize: "17px", fontWeight: 800, color: "#111827" },
  planTagline: { margin: "3px 0 0", fontSize: "12px", color: "#9ca3af", fontWeight: 600 },

  planPriceRow: { display: "flex", alignItems: "baseline", gap: "6px", marginBottom: "18px" },
  planPrice:    { fontSize: "30px", fontWeight: 800, color: "#111827", letterSpacing: "-0.5px" },
  planPeriod:   { fontSize: "13px", color: "#9ca3af", fontWeight: 600 },

  planDivider: { height: "1px", background: "#eee", border: "none", marginBottom: "18px" },

  featureList: { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "12px", flex: 1, marginBottom: "22px" },
  featureItem: { display: "flex", alignItems: "flex-start", gap: "9px" },
  featureText: { fontSize: "13px", color: "#374151", lineHeight: 1.5 },

  planCta: { borderRadius: "12px", padding: "13px 0", fontSize: "14px", fontWeight: 800, cursor: "pointer", background: "#fff", width: "100%" },
  detailsBtn: { borderRadius: "12px", padding: "12px 0", fontSize: "13px", fontWeight: 800, cursor: "pointer", background: "#fff", width: "100%", marginBottom: "10px" },

  serviceImageGrid:  { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(64px, 72px))", gap: "10px" },
  serviceImageCard:  { borderRadius: "10px", overflow: "hidden", background: "#f8fafc", border: "1px solid #eef0f3" },
  serviceImageThumb: { width: "100%", aspectRatio: "1 / 1", objectFit: "cover", display: "block" },
  serviceImageLabel: { display: "flex", alignItems: "center", justifyContent: "center", gap: "5px", fontSize: "11px", fontWeight: 700, padding: "7px 6px", textAlign: "center", lineHeight: 1.2 },

  // ── Plan details modal ─────────────────────────────────────────────────────
  modalOverlay:  { position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: "16px", zIndex: 9999 },
  modalCard:     { width: "min(640px,100%)", maxHeight: "90vh", overflowY: "auto", background: "#fff", borderRadius: "22px", border: "1px solid #e2e8f0", boxShadow: "0 24px 60px rgba(15,23,42,0.2)", position: "relative" },
  modalCloseBtn: { position: "absolute", top: "14px", right: "14px", width: "36px", height: "36px", borderRadius: "10px", border: "none", background: "rgba(255,255,255,0.9)", cursor: "pointer", fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.12)" },

  modalSliderWrap: { position: "relative", width: "100%", aspectRatio: "16 / 9", background: "#f1f5f9", overflow: "hidden", borderRadius: "22px 22px 0 0" },
  modalSliderImg:  { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  modalArrow:      { position: "absolute", top: "50%", transform: "translateY(-50%)", width: "34px", height: "34px", borderRadius: "50%", border: "none", background: "rgba(0,0,0,0.45)", color: "#fff", fontSize: "20px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, lineHeight: 1 },
  modalDots:       { position: "absolute", bottom: "10px", left: "50%", transform: "translateX(-50%)", display: "flex", gap: "6px" },
  modalDot:        { width: "8px", height: "8px", borderRadius: "50%", border: "none", background: "rgba(255,255,255,0.55)", cursor: "pointer", padding: 0 },
  modalPopularTag: { position: "absolute", top: "14px", left: "14px", display: "inline-flex", alignItems: "center", gap: "5px", color: "#fff", fontSize: "11px", fontWeight: 800, padding: "6px 12px", borderRadius: "999px" },

  modalBody:  { padding: "22px 24px 26px" },
  modalHeadRow: { display: "flex", alignItems: "flex-start", gap: "12px", marginBottom: "14px" },
  modalTitle: { margin: 0, fontSize: "22px", fontWeight: 800, color: "#111827" },

  modalPriceRow: { display: "flex", alignItems: "baseline", gap: "6px", marginBottom: "18px" },
  modalPrice:    { fontSize: "32px", fontWeight: 800, color: "#111827", letterSpacing: "-0.5px" },

  modalStatsRow: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px", background: "#f8fafc", borderRadius: "14px", padding: "14px 12px", marginBottom: "20px" },
  modalStat:     { display: "flex", alignItems: "center", gap: "8px" },
  modalStatVal:  { fontSize: "13px", fontWeight: 800, color: "#111827" },
  modalStatLabel:{ fontSize: "10px", color: "#9ca3af" },

  modalSection:      { marginBottom: "20px" },
  modalSectionTitle: { fontSize: "13px", fontWeight: 800, color: "#111827", marginBottom: "12px" },

  trustStrip: { marginTop: "12px", background: "#fff", border: "1px solid #ececec", borderRadius: "20px", padding: "22px 26px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "18px" },
  trustItem: { display: "flex", alignItems: "center", gap: "12px" },
  trustIconWrap: { width: "38px", height: "38px", borderRadius: "50%", background: "#eef2ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  trustTitle: { fontSize: "13px", fontWeight: 800, color: "#111827" },
  trustText:  { fontSize: "12px", color: "#6b7280", marginTop: "1px" },

  supportBox:   { marginTop: "24px", background: "#f5f6fb", borderRadius: "20px", padding: "28px 30px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "20px", flexWrap: "wrap" },
  supportTitle: { margin: 0, fontSize: "20px", fontWeight: 800, color: "#111827" },
  supportText:  { margin: "6px 0 0", color: "#6b7280", fontSize: "14px" },
  supportBtn:   { display: "inline-flex", alignItems: "center", gap: "8px", background: "#4f46e5", color: "#fff", padding: "14px 22px", borderRadius: "12px", fontWeight: 700, fontSize: "15px", textDecoration: "none", whiteSpace: "nowrap" },
};

export default SubscriptionPlans;
